import { ROUTES } from './routes.config';
import type { RrmsRole } from './app.config';

/** System-level admin permissions (Admin Console). */
export const ADMIN_PERMISSIONS = {
  templates: 'manage_templates',
  nodes: 'manage_nodes',
  roles: 'manage_roles',
  users: 'manage_users',
  relationships: 'manage_relationships',
} as const;

/** Portal record permissions (main RRMS app). */
export const PORTAL_PERMISSIONS = {
  view: 'records.view',
  upload: 'records.upload',
  approve: 'records.approve',
  decideAccess: 'records.decide_access',
  manageContent: 'records.manage_content',
  reports: 'records.reports',
} as const;

export const ADMIN_NAV = [
  { to: '/admin/templates', label: 'Template Builder', permission: ADMIN_PERMISSIONS.templates },
  { to: '/admin/hierarchy', label: 'Hierarchy Builder', permission: ADMIN_PERMISSIONS.nodes },
  { to: '/admin/roles', label: 'Role Builder', permission: ADMIN_PERMISSIONS.roles },
  { to: '/admin/users', label: 'User Management', permission: ADMIN_PERMISSIONS.users },
] as const;

/** Legacy-role fallbacks while CMS/bypass sessions carry no permission array. */
export const PORTAL_VIEW_FALLBACK: RrmsRole[] = ['RRMS_USER', 'RRMS_CONTENT_MANAGER', 'RRMS_SUPER_ADMIN'];
export const PORTAL_UPLOAD_FALLBACK: RrmsRole[] = ['RRMS_USER', 'RRMS_CONTENT_MANAGER'];
export const PORTAL_APPROVE_FALLBACK: RrmsRole[] = ['RRMS_CONTENT_MANAGER', 'RRMS_SUPER_ADMIN'];
export const PORTAL_MANAGE_CONTENT_FALLBACK: RrmsRole[] = ['RRMS_CONTENT_MANAGER'];

export function firstAdminPath(
  has: (key: string) => boolean,
  permissions: string[] = []
): string | null {
  for (const item of ADMIN_NAV) {
    if (item.permission === ADMIN_PERMISSIONS.nodes) {
      if (canAccessHierarchyBuilder(permissions, has)) return item.to;
    } else if (has(item.permission)) {
      return item.to;
    }
  }
  return null;
}

/**
 * Hierarchy Builder: `manage_nodes` opens the module; instance access is
 * controlled per template (`{template_key}.view`, etc.).
 */
export function canAccessHierarchyBuilder(
  permissions: string[],
  has: (key: string) => boolean
): boolean {
  if (has('*') || has(ADMIN_PERMISSIONS.nodes)) return true;
  return permissions.some(
    (p) => p.endsWith('.view') && p !== PORTAL_PERMISSIONS.view && !p.startsWith('manage_')
  );
}

export function templatePermissionKey(templateKey: string, action: string): string {
  return `${templateKey}.${action}`;
}

export interface PortalAccessFlags {
  canView: boolean;
  canUpload: boolean;
  canApprove: boolean;
  canDecideAccess: boolean;
  canManageContent: boolean;
  canAccessRequest: boolean;
  canViewReports: boolean;
  isAdmin: boolean;
}

export function buildPortalAccess(input: {
  has: (key: string) => boolean;
  can: (key: string, fallback?: RrmsRole[]) => boolean;
  isSuperAdmin: boolean;
  permissions: string[];
  role: RrmsRole | null;
}): PortalAccessFlags {
  const { has, can, isSuperAdmin, permissions, role } = input;
  const legacy = permissions.length === 0 && !isSuperAdmin;

  return {
    canView: can(PORTAL_PERMISSIONS.view, PORTAL_VIEW_FALLBACK),
    canUpload: can(PORTAL_PERMISSIONS.upload, PORTAL_UPLOAD_FALLBACK),
    canApprove: can(PORTAL_PERMISSIONS.approve, PORTAL_APPROVE_FALLBACK),
    canDecideAccess: can(PORTAL_PERMISSIONS.decideAccess, PORTAL_APPROVE_FALLBACK),
    canManageContent: can(PORTAL_PERMISSIONS.manageContent, PORTAL_MANAGE_CONTENT_FALLBACK),
    // New dynamic-RBAC-only permission — no legacy-role fallback, since it has
    // no equivalent in the pre-admin-module role system.
    canViewReports: can(PORTAL_PERMISSIONS.reports),
    canAccessRequest: legacy
      ? role === 'RRMS_USER'
      : !has(PORTAL_PERMISSIONS.approve) && !isSuperAdmin,
    isAdmin: legacy ? role === 'RRMS_SUPER_ADMIN' : isSuperAdmin,
  };
}

/** First sidebar route the user is allowed to open (fallback when a guard denies access). */
export function firstPortalPath(access: PortalAccessFlags): string {
  if (access.canView) return ROUTES.DASHBOARD;
  if (access.canUpload) return ROUTES.UPLOAD;
  if (access.canManageContent) return ROUTES.CONTENT_MGMT;
  if (access.canAccessRequest) return ROUTES.ACCESS_REQUEST;
  return ROUTES.NOTIFICATIONS;
}
